import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ResponsiveLogo } from '../components/ThemeLogo';
import PublicHeader from '../components/PublicHeader';
import {
  Target, Rocket, MessageCircle, Layout, Cpu, Zap, Star,
  Building2, Brain, Lightbulb, Shield, Users, Columns3,
  Sparkles, Database, Compass, TrendingUp, ArrowRight,
  ChevronRight, KanbanSquare,
} from 'lucide-react';

const ICON_MAP = {
  target: Target, rocket: Rocket, 'message-circle': MessageCircle,
  layout: Layout, cpu: Cpu, zap: Zap, star: Star, building: Building2,
  brain: Brain, lightbulb: Lightbulb, shield: Shield, users: Users,
  kanban: KanbanSquare, sparkles: Sparkles, database: Database,
  compass: Compass, 'trending-up': TrendingUp, columns: Columns3,
};

function getIcon(name) {
  return ICON_MAP[name] || Star;
}

// ── Scroll animation hook ──
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ═══════════════════════════════════════
// SECTION RENDERERS
// ═══════════════════════════════════════

function HeroSection({ section }) {
  const [ref, vis] = useInView(0.1);
  return (
    <section ref={ref} className="relative overflow-hidden bg-gradient-to-br from-odapto-orange/5 via-background to-odapto-teal/5 py-20 md:py-28 px-4 sm:px-6" data-testid="about-hero">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className={`transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {section.badge && (
              <span className="inline-block text-xs font-semibold tracking-wider uppercase text-odapto-orange bg-odapto-orange/10 px-3 py-1 rounded-full mb-5">
                {section.badge}
              </span>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 tracking-tight">
              {section.heading}
            </h1>
            <div className="text-muted-foreground text-base sm:text-lg leading-relaxed space-y-4 mb-8">
              {(section.description || '').split('\n\n').map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              {section.primary_cta && (
                <Link to={section.primary_cta.href || '/register'}>
                  <Button size="lg" className="bg-odapto-orange hover:bg-odapto-orange-hover text-white px-6 h-12 text-base" data-testid="about-cta-primary">
                    {section.primary_cta.label} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              {section.secondary_cta && (
                <Link to={section.secondary_cta.href || '/strategy-coach'}>
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 px-6 h-12 text-base" data-testid="about-cta-secondary">
                    {section.secondary_cta.label}
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div className={`transition-all duration-700 delay-200 ${vis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {section.image ? (
              <img src={section.image} alt="Odapto Strategy" className="w-full max-w-lg mx-auto rounded-2xl" loading="lazy" />
            ) : (
              <div className="w-full max-w-lg mx-auto aspect-[4/3] rounded-2xl bg-gradient-to-br from-odapto-orange/20 to-odapto-teal/20 flex items-center justify-center">
                <Brain className="w-24 h-24 text-odapto-orange/40" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function MissionVisionSection({ section }) {
  const [ref, vis] = useInView();
  const cards = section.cards || [];
  return (
    <section ref={ref} className="py-20 md:py-24 px-4 sm:px-6 bg-muted/30" data-testid="about-mission-vision">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {cards.map((card, i) => {
            const Icon = getIcon(card.icon);
            return (
              <div
                key={i}
                className={`p-8 rounded-2xl bg-card border border-border shadow-sm hover:shadow-lg transition-all duration-500 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-odapto-orange/10 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-odapto-orange" />
                </div>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{card.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BeliefsSection({ section }) {
  const [ref, vis] = useInView();
  const cards = section.cards || [];
  return (
    <section ref={ref} className="py-20 md:py-24 px-4 sm:px-6" data-testid="about-beliefs">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">{section.heading}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card, i) => {
            const Icon = getIcon(card.icon);
            return (
              <div
                key={i}
                className={`group p-6 rounded-2xl border border-border bg-card hover:border-odapto-orange/40 hover:shadow-lg transition-all duration-500 ${i === cards.length - 1 && cards.length % 3 === 2 ? 'sm:col-span-2 lg:col-span-1' : ''} ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-odapto-teal/10 group-hover:bg-odapto-orange/10 flex items-center justify-center mb-4 transition-colors">
                  <Icon className="w-5 h-5 text-odapto-teal group-hover:text-odapto-orange transition-colors" />
                </div>
                <p className="text-base font-medium leading-relaxed">{card.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function DifferentiatorsSection({ section }) {
  const [ref, vis] = useInView();
  const cards = section.cards || [];
  return (
    <section ref={ref} className="py-20 md:py-28 px-4 sm:px-6 bg-[#162F38] text-white" data-testid="about-differentiators">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">{section.heading}</h2>
          <div className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto space-y-2">
            {(section.description || '').split('\n').map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card, i) => {
            const Icon = getIcon(card.icon);
            return (
              <div
                key={i}
                className={`group p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-odapto-orange/40 transition-all duration-500 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-odapto-orange/20 group-hover:bg-odapto-orange/30 flex items-center justify-center mb-4 transition-colors">
                  <Icon className="w-5 h-5 text-odapto-orange" />
                </div>
                <h3 className="font-bold text-lg mb-2">{card.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{card.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HighlightsSection({ section }) {
  const [ref, vis] = useInView();
  const cards = section.cards || [];
  return (
    <section ref={ref} className="py-20 md:py-24 px-4 sm:px-6 bg-muted/30" data-testid="about-highlights">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">{section.heading}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((card, i) => {
            const Icon = getIcon(card.icon);
            return (
              <div
                key={i}
                className={`group p-6 rounded-2xl bg-card border border-border hover:shadow-lg hover:border-odapto-teal/40 transition-all duration-500 text-center ${vis ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-odapto-orange/10 to-odapto-teal/10 group-hover:from-odapto-orange/20 group-hover:to-odapto-teal/20 flex items-center justify-center mx-auto mb-4 transition-colors">
                  <Icon className="w-7 h-7 text-odapto-orange" />
                </div>
                <h3 className="font-bold text-base mb-2">{card.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{card.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTASection({ section }) {
  const [ref, vis] = useInView();
  return (
    <section ref={ref} className="relative py-20 md:py-28 px-4 sm:px-6 overflow-hidden" data-testid="about-cta">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-odapto-orange/10 via-background to-odapto-teal/10" />
      <div className="absolute top-10 left-10 w-72 h-72 bg-odapto-orange/5 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-odapto-teal/5 rounded-full blur-3xl" />

      <div className={`relative max-w-3xl mx-auto text-center transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-5">{section.heading}</h2>
        <p className="text-muted-foreground text-base sm:text-lg mb-8 max-w-xl mx-auto">{section.description}</p>
        <div className="flex flex-wrap justify-center gap-3">
          {section.primary_cta && (
            <Link to={section.primary_cta.href || '/register'}>
              <Button size="lg" className="bg-odapto-orange hover:bg-odapto-orange-hover text-white px-8 h-12 text-base">
                {section.primary_cta.label} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
          {section.secondary_cta && (
            <Link to={section.secondary_cta.href || '/register'}>
              <Button size="lg" variant="outline" className="px-8 h-12 text-base">
                {section.secondary_cta.label}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// Section type → renderer map
const SECTION_RENDERERS = {
  hero: HeroSection,
  mission_vision: MissionVisionSection,
  beliefs: BeliefsSection,
  differentiators: DifferentiatorsSection,
  highlights: HighlightsSection,
  cta: CTASection,
};


// ═══════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════

export default function AboutPage() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/pages/about-us`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setPage)
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, []);

  // SEO
  useEffect(() => {
    if (!page?.seo) return;
    document.title = page.seo.meta_title || 'About Odapto';
    const setMeta = (name, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', page.seo.meta_description);
    setMeta('keywords', (page.seo.keywords || []).join(', '));
    setMeta('og:title', page.seo.meta_title);
    setMeta('og:description', page.seo.meta_description);
    if (page.seo.og_image) setMeta('og:image', page.seo.og_image);
  }, [page]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-10 h-10 border-4 border-odapto-orange border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!page) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
        <Link to="/"><Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white">Go Home</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <PublicHeader />

      {/* Dynamic Sections */}
      {(page.sections || []).map((section, i) => {
        const Renderer = SECTION_RENDERERS[section.type];
        if (!Renderer) return null;
        return <Renderer key={i} section={section} />;
      })}

      {/* Footer */}
      <footer className="bg-[#162F38] text-zinc-400 py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/"><ResponsiveLogo className="h-7 w-auto brightness-200" /></Link>
            <div className="flex items-center gap-6 text-xs">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link to="/about-us" className="hover:text-white transition-colors">About</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
            <span className="text-xs">&copy; 2026 Odapto. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
